/**
 * The home address form's state and rules (Set up My Home and Home
 * Settings, 2026-10-02): the draft HomeAddressFields edits, how a stored
 * address becomes one, the body POST and PATCH /me/home receive, and the
 * translated text for each @evi/shared address rule. Pure, so the form
 * and its tests share it.
 */
import type { TFunction } from 'i18next';
import {
  homeCountryFromAny, resolveHomeRegion, validateHomeAddress,
  type HomeAddressErrorCode, type HomeCountry,
} from '@evi/shared';
import type { HomeAddress } from '@/lib/api';

export interface HomeAddressDraft {
  country: HomeCountry | null;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export const EMPTY_HOME_ADDRESS: HomeAddressDraft = { country: null, street: '', city: '', state: '', zip: '' };

/** A stored address as a draft. The country is the one the stored state
 *  names, else the stored name (the Api's rule: a recognized state
 *  outranks a stored country, so an Ontario home stored against United
 *  States opens as Canada); a home that has neither starts unpicked. */
export function homeAddressDraftFrom(address: HomeAddress | null | undefined): HomeAddressDraft {
  if (!address) return EMPTY_HOME_ADDRESS;
  return {
    country: resolveHomeRegion(address.state)?.country ?? homeCountryFromAny(address.country) ?? null,
    street: address.street ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? '',
  };
}

/** The body half for POST / PATCH /me/home, or null while the draft is
 *  incomplete or invalid. */
export function homeAddressForBody(d: HomeAddressDraft): {
  street: string; city: string; state: string; zip: string; country: HomeCountry;
} | null {
  if (!d.country || !d.street.trim() || !d.city.trim()) return null;
  const check = validateHomeAddress({ country: d.country, state: d.state, zip: d.zip });
  if (!check.ok) return null;
  return {
    street: d.street.trim(),
    city: d.city.trim(),
    state: check.regionName,
    zip: check.zip,
    country: check.country,
  };
}

/** The region code a draft resolves to, for the time zone check. */
export function homeAddressRegionCode(d: HomeAddressDraft): string | null {
  return d.country ? resolveHomeRegion(d.state, d.country)?.code ?? null : null;
}

/**
 * Translated text for an address rule's refusal, from this form or from
 * the Api's `error.reason`. `typedState` names the region in the
 * wrong-country case.
 */
export function homeAddressErrorText(
  t: TFunction<'homeAddress'>,
  code: HomeAddressErrorCode | string,
  country: HomeCountry | null,
  typedState?: string,
): string | null {
  const ca = country === 'CA';
  switch (code) {
    case 'STATE_REQUIRED': return ca ? t('errors.provinceRequired') : t('errors.stateRequired');
    case 'STATE_UNKNOWN': return ca ? t('errors.provinceUnknown') : t('errors.stateUnknown');
    case 'STATE_OR_PROVINCE_REQUIRED': return t('errors.stateOrProvince');
    case 'STATE_WRONG_COUNTRY': {
      const other = resolveHomeRegion(typedState, ca ? 'US' : 'CA');
      const name = other?.name ?? typedState ?? '';
      return ca ? t('errors.stateInUs', { name }) : t('errors.provinceInCanada', { name });
    }
    case 'ZIP_REQUIRED': return ca ? t('errors.postalRequired') : t('errors.zipRequired');
    case 'ZIP_INVALID': return t('errors.zipInvalid');
    case 'POSTAL_INVALID': return t('errors.postalInvalid');
    default: return null;
  }
}

/** "Northwest Territories or Nunavut", "Denver or Chicago" in the
 *  reader's language. */
export function formatOrList(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { type: 'disjunction' }).format(items);
  } catch {
    return items.join(' / ');
  }
}
